import { useState, useEffect, useRef, useMemo } from 'react';
import { Drawer, Typography, IconButton, Stack, CircularProgress, Tooltip, Select, MenuItem, ToggleButtonGroup, ToggleButton } from '@mui/material';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import HoverPreview from './hoverPreview';
import CreatorFilter from './creatorFilter';
import useCachedImage from '../hooks/useCachedImage';
import { t } from '../utils/strings';
import { accent, accentSurface } from '../utils/colorDict';

const SearchThumb = ({ item, mapRef, handleModalData, handleMarkedThumbnail, handleItemToPosition, cancelBeam, authData, geotaggedIds, removedLocationIds, itemToPosition, handleRightPanel, geotagSourcePanel, lang, onOpen }) => {
  const imgUrl = item.properties.right_panel_url || item.properties.thumbnail_url;
  const imgFallback = item.properties.right_panel_fallback || item.properties.thumbnail_fallback;
  const itemId = item.properties?.id || item.id;
  const originallyHasLocation = item?.geometry?.coordinates?.length > 0;
  const wasGeotagged = geotaggedIds.has(itemId);
  const wasRemoved = removedLocationIds.has(itemId);
  const hasLocation = (originallyHasLocation && !wasRemoved) || wasGeotagged;
  const isQuestionable = Array.isArray(item.properties.questionable_by) ? item.properties.questionable_by.length > 0 : false;
  const { image, isError } = useCachedImage(imgUrl || null, imgFallback || null);
  const blobSrc = useMemo(() => image ? URL.createObjectURL(image) : null, [image]);
  useEffect(() => { return () => { if (blobSrc) URL.revokeObjectURL(blobSrc); }; }, [blobSrc]);

  const handleClick = () => {
    if (originallyHasLocation) {
      handleMarkedThumbnail(item);
      mapRef.easeTo({ zoom: 17, center: [item.geometry.coordinates[0], item.geometry.coordinates[1]] });
    }
    // Always open the modal via the search-list nav so arrow keys cycle
    // through the current search results (not the map-proximity list).
    onOpen(item);
  };

  const handleGeotagClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleModalData(null, 1);
    cancelBeam();
    geotagSourcePanel.current = 5;
    handleRightPanel(0);
    handleItemToPosition(item);
  };

  return (
    <HoverPreview previewUrl={item.properties.modal_url} style={{ width: 'calc(25% - 5px)' }} disabled={!blobSrc || !!itemToPosition} previewHeight={300}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: blobSrc ? '#444' : '#e0e0e0',
          cursor: 'pointer',
          overflow: 'hidden'
        }}
        onClick={handleClick}
      >
        {isQuestionable && <ReportProblemIcon sx={{ position: 'absolute', top: 2, right: 2, zIndex: 2, color: '#ff9800', fontSize: 14 }} />}
        {blobSrc && (hasLocation
          ? <LocationOnIcon sx={{ position: 'absolute', top: 2, left: 2, zIndex: 2, color: '#90ee90', fontSize: 14 }} />
          : (authData?.user?.role === 'admin' || authData?.user?.role === 'editor')
            ? <Tooltip title={t('addLocation', lang)}>
                <AddLocationAltIcon
                  onClick={handleGeotagClick}
                  sx={{ position: 'absolute', top: 2, left: 2, zIndex: 2, color: '#ff6b6b', fontSize: 16, cursor: 'pointer', '&:hover': { color: '#ff4040' } }}
                />
              </Tooltip>
            : <LocationOffIcon sx={{ position: 'absolute', top: 2, left: 2, zIndex: 2, color: 'rgba(255,255,255,0.6)', fontSize: 14 }} />
        )}
        {!blobSrc && !isError && !(!imgUrl) && <CircularProgress size={18} sx={{ position: 'absolute' }} />}
        {(isError || !imgUrl) && !blobSrc && <BrokenImageIcon sx={{ position: 'absolute', color: '#bbb', fontSize: 24 }} />}
        {blobSrc && <img src={blobSrc}
          alt=""
          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
        />}
      </div>
    </HoverPreview>
  );
};

const SEARCH_BATCH = 80;

const SearchWidget = ({ mapRef, items, handleSearch, rightPanel, handleModalData, openModalWithList, handleRightPanel, handleMarkedThumbnail, handleItemToPosition, cancelBeam, handleItemsToMark, authData, lastGeotaggedId, lastRemovedLocationId, itemToPosition, geotagSourcePanel, lang, creatorOptions, widgetCreators, setWidgetCreators }) => {
  const [batch, setBatch] = useState(0);
  const [geotaggedIds, setGeotaggedIds] = useState<Set<number>>(new Set());
  const [removedLocationIds, setRemovedLocationIds] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<'all' | 'tagged' | 'untagged'>('all');
  const scrollRef = useRef(null);

  useEffect(() => { setBatch(0); setGeotaggedIds(new Set()); setRemovedLocationIds(new Set()); setFilter('all'); }, [items]);
  useEffect(() => { scrollRef.current?.scrollTo(0, 0); }, [batch]);

  useEffect(() => {
    if (!lastGeotaggedId) return;
    setGeotaggedIds(prev => { const next = new Set(prev); next.add(lastGeotaggedId); return next; });
    setRemovedLocationIds(prev => { const next = new Set(prev); next.delete(lastGeotaggedId); return next; });
  }, [lastGeotaggedId]);

  useEffect(() => {
    if (!lastRemovedLocationId) return;
    setRemovedLocationIds(prev => { const next = new Set(prev); next.add(lastRemovedLocationId); return next; });
    setGeotaggedIds(prev => { const next = new Set(prev); next.delete(lastRemovedLocationId); return next; });
  }, [lastRemovedLocationId]);

  // Filter items based on geotag status
  const filteredItems = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(item => {
      const itemId = item.properties?.id || item.id;
      const originallyHasLocation = item?.geometry?.coordinates?.length > 0;
      const wasGeotagged = geotaggedIds.has(itemId);
      const wasRemoved = removedLocationIds.has(itemId);
      const hasLocation = (originallyHasLocation && !wasRemoved) || wasGeotagged;
      return filter === 'tagged' ? hasLocation : !hasLocation;
    });
  }, [items, filter, geotaggedIds, removedLocationIds]);

  const totalBatches = Math.max(1, Math.ceil(filteredItems.length / SEARCH_BATCH));
  const batchItems = filteredItems.slice(batch * SEARCH_BATCH, (batch + 1) * SEARCH_BATCH);

  // Reset batch when filter changes
  useEffect(() => { setBatch(0); }, [filter]);

  // Always open from search via openModalWithList so left/right arrow keys
  // cycle through the current search-results batch (not the map-proximity
  // list openModalWithNav would build).
  const openFromSearch = (item: any) => {
    if (openModalWithList) {
      openModalWithList(item, batchItems);
    } else {
      handleModalData(item, item?.properties?.mediatype);
    }
  };

  const taggedCount = useMemo(() => items.filter(item => {
    const itemId = item.properties?.id || item.id;
    const orig = item?.geometry?.coordinates?.length > 0;
    return (orig && !removedLocationIds.has(itemId)) || geotaggedIds.has(itemId);
  }).length, [items, geotaggedIds, removedLocationIds]);

  const untaggedCount = items.length - taggedCount;

  return <Drawer
    PaperProps={{ style: { height: 'calc(100% - 80px)', top: '80px', zIndex: 3, width: '34vw', overflowX: 'hidden', background: 'whitesmoke' } }}
    variant="persistent"
    anchor={'left'}
    onClose={() => { handleRightPanel(0) }}
    open={rightPanel === 5}
  >
    <Stack sx={{ backgroundColor: accentSurface, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack sx={{ padding: '10px 10px 0', alignItems: 'center', flexShrink: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', zIndex: 1, backgroundColor: accentSurface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography variant='h6'>{t('searchResults', lang)}</Typography>
          {/* Per-widget creator filter — independent of the header dropdown once open. */}
          <CreatorFilter
            creatorOptions={creatorOptions}
            selected={Array.isArray(widgetCreators) ? widgetCreators : []}
            onChange={setWidgetCreators}
            lang={lang}
          />
        </div>
        <Typography variant="caption" color="text.secondary" sx={{ paddingBottom: '4px' }}>{t('itemsFound', lang, { count: items.length, s: items.length !== 1 ? 's' : '' })}</Typography>

        {/* Filter toggle */}
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => { if (v) setFilter(v); }}
          size="small"
          sx={{ mb: 0.5, direction: lang === 'he' ? 'rtl' : 'ltr', '& .MuiToggleButton-root': { py: '1px', px: '8px', fontSize: '0.7rem', textTransform: 'none' } }}
        >
          <ToggleButton value="all">{lang === 'he' ? 'הכול' : 'All'} ({items.length})</ToggleButton>
          <ToggleButton value="tagged"><LocationOnIcon sx={{ fontSize: 14, mr: 0.3, color: '#4caf50' }} />{taggedCount}</ToggleButton>
          <ToggleButton value="untagged"><LocationOffIcon sx={{ fontSize: 14, mr: 0.3, color: '#f44336' }} />{untaggedCount}</ToggleButton>
        </ToggleButtonGroup>

        {totalBatches > 1 && <div dir={lang === 'he' ? 'rtl' : 'ltr'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', margin: '4px 0' }}>
          <IconButton size="small" style={{ color: accent }} disabled={batch === 0}
            onClick={() => setBatch(b => Math.max(0, b - 1))}>{lang === 'he' ? <ArrowRightIcon /> : <ArrowLeftIcon />}</IconButton>
          <Select size="small" value={batch} onChange={(e) => setBatch(e.target.value as number)}
            sx={{ minWidth: 60, fontSize: '0.85rem', '& .MuiSelect-select': { py: '2px' } }}>
            {Array.from({ length: totalBatches }, (_, i) => (
              <MenuItem key={i} value={i}>{i + 1}</MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">/ {totalBatches}</Typography>
          <IconButton size="small" style={{ color: accent }} disabled={batch >= totalBatches - 1}
            onClick={() => setBatch(b => Math.min(totalBatches - 1, b + 1))}>{lang === 'he' ? <ArrowLeftIcon /> : <ArrowRightIcon />}</IconButton>
        </div>}
        {filteredItems.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ pb: 0.5 }}>
          {t('itemsRange', lang, { from: batch * SEARCH_BATCH + 1, to: Math.min((batch + 1) * SEARCH_BATCH, filteredItems.length), total: filteredItems.length })}
        </Typography>}
      </Stack>

      <div ref={scrollRef} dir={lang === 'he' ? 'rtl' : 'ltr'} style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '10px', overflow: 'auto', flex: 1, alignContent: 'flex-start' }}>
        {batchItems.map((item) =>
          <SearchThumb
            key={item.properties.id || item.id}
            item={item}
            mapRef={mapRef}
            handleModalData={handleModalData}
            onOpen={openFromSearch}
            handleMarkedThumbnail={handleMarkedThumbnail}
            handleItemToPosition={handleItemToPosition}
            cancelBeam={cancelBeam}
            authData={authData}
            geotaggedIds={geotaggedIds}
            removedLocationIds={removedLocationIds}
            itemToPosition={itemToPosition}
            handleRightPanel={handleRightPanel}
            geotagSourcePanel={geotagSourcePanel}
            lang={lang}
          />
        )}
      </div>
    </Stack>
  </Drawer>
}

export default SearchWidget
